/**
 * A barcode as a standalone picture.
 *
 * The canvas renders barcodes into a live <svg> through JsBarcode, which is fine
 * for painting but useless to anything that needs the bars as an IMAGE — a mask,
 * for one. So the same call is made against a detached <svg>, serialised, and
 * handed back as a data URL.
 *
 * Cached per (value + format + bar styling): serialising is cheap but not free,
 * and a mask is rebuilt on every render of the layer it clips.
 */
import JsBarcode from 'jsbarcode';

const cache = new Map();
const CACHE_MAX = 60;

function optionsFor(el) {
    const styles = el?.styles || {};
    return {
        format: el?.format || 'CODE128',
        lineColor: styles.lineColor || '#000000',
        background: styles.background || '#ffffff',
        displayValue: styles.displayValue !== false,
        margin: styles.margin ?? 4,
        height: Math.max(20, (Number(el?.height) || 70) - 20),
        width: styles.barWidth ?? 1.6,
    };
}

/**
 * `null` when the value is one this format cannot encode — the caller then falls
 * back to the element's box rather than showing an empty picture.
 */
export function barcodeSvgDataUrl(el) {
    if (typeof document === 'undefined') return null;
    const value = String(el?.content || '0');
    const opts = optionsFor(el);
    const key = JSON.stringify([value, opts]);
    if (cache.has(key)) return cache.get(key);

    let url = null;
    try {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        JsBarcode(svg, value, opts);
        svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        const markup = new XMLSerializer().serializeToString(svg);
        url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    } catch {
        url = null; // invalid content for this symbology
    }

    if (cache.size > CACHE_MAX) cache.clear();
    cache.set(key, url);
    return url;
}
